import React, { useEffect } from 'react';
import {
  Container,
  StyledBreadcrumbs,
  MainTitle,
  ReviewSection,
  LeftColumn,
  RightColumn,
  PaymentShippingDetailsWrapper
} from './styles';
import TermsofServiceComponent from './TermsofService/TermsofServiceComponent';
import PlanSummary from './PlanSummary/PlanSummary';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';
import { PaymentMethod } from '../PaymentMethod';
import { ShippingAddress } from '../ShippingAddress';
import {
  SubscriptionEntityTypeEnum,
  SubscriptionStateResponse
} from 'src/types/subscription';
import { useShippingAddress } from 'src/hooks/useShippingAddress';
import useShellContext from 'src/hooks/useShellContext';
import useBillingClient from 'src/hooks/useUsageClient';
import { useData } from 'src/contexts/DataContext';
import { PrinterItem } from 'src/utils/Products/productFilter';
import RequestErrorModal from '../RequestErrorModal';
import { useCreateCart } from 'src/hooks/useCreateCart';
import { LoadingHandler } from '../LoadingHandler';
import useFlags from 'src/hooks/useFlags';

interface UpgradeReviewProps {
  onBack?: () => void;
  onConfirm?: () => void;
  subscriptions?: SubscriptionStateResponse[];
  selectedPrinterDetails?: PrinterItem;
  currentPrinterDetails?: PrinterItem[];
  setOrderNumber?: (orderNumber: string) => void;
}

const UpgradeReview = ({
  onBack,
  onConfirm,
  subscriptions,
  selectedPrinterDetails,
  currentPrinterDetails,
  setOrderNumber
}: UpgradeReviewProps): JSX.Element => {
  const getText = useTranslationHelper();
  const { authProvider, stack } = useShellContext();
  const {
    isLoading,
    error: cartCreationError,
    cartInfo,
    createCart,
    setError: setCartCreationError
  } = useCreateCart({
    subscriptions,
    selectedPrinterDetails
  });
  const { flags } = useFlags();
  const { setUsageData, setCurrentUsageData, setRolloverData } = useData();
  const { fetchAllUsageData } = useBillingClient({
    isFullUpgrade: flags?.PF_enableFullUpgrade,
    setUsageData,
    setCurrentUsageData,
    setRolloverData
  });
  const paymentMethodId = subscriptions?.[0]?.paymentMethodId || '';
  const shippingAddressId =
    subscriptions?.[0]?.entities?.[0]?.shippingAddressId || '';

  const { shippingAddress, loading, error } = useShippingAddress({
    authProvider,
    stack,
    shippingAddressId
  });

  const instantInkEntity = subscriptions?.[0]?.entities?.find(
    (entity) => entity?.entityType === SubscriptionEntityTypeEnum.INSTANT_INK
  );

  useEffect(() => {
    fetchAllUsageData(
      subscriptions?.[0]?.subscriptionId,
      instantInkEntity?.entityId
    );
  }, []);

  if (isLoading) {
    return <LoadingHandler loading={isLoading} />;
  } else
    return (
      <>
        {cartCreationError && (
          <RequestErrorModal
            show={cartCreationError}
            isLoading={isLoading}
            onClose={() => setCartCreationError(false)}
            onProceed={() => {
              createCart();
            }}
          />
        )}
        <Container>
          <StyledBreadcrumbs>
            {getText('step2Of2', 'Step 2 of 2')}
          </StyledBreadcrumbs>
          <MainTitle data-testid="upgradeReviewHeader">
            Review your upgrade
          </MainTitle>

          <ReviewSection>
            <LeftColumn>
              <PlanSummary
                selectedPrinterDetails={selectedPrinterDetails}
                currentPrinterDetails={currentPrinterDetails}
                cartInfo={cartInfo}
              />
              <PaymentShippingDetailsWrapper>
                <PaymentMethod paymentMethodId={paymentMethodId} />
                <ShippingAddress
                  shippingAddress={shippingAddress}
                  isLoading={loading}
                  error={error}
                />
              </PaymentShippingDetailsWrapper>
            </LeftColumn>

            <RightColumn>
              <TermsofServiceComponent
                onBack={onBack}
                onConfirm={onConfirm}
                selectedPrinterDetails={selectedPrinterDetails}
                cartInfo={cartInfo}
                setOrderNumber={setOrderNumber}
              />
            </RightColumn>
          </ReviewSection>
        </Container>
      </>
    );
};

export default UpgradeReview;
